import moment from "moment"
import { toCodePoints } from "twemoji-parser"
import { BsFileEarmark, BsFiletypeAi, BsFiletypeBmp, BsFiletypeCsv, BsFiletypeDoc, BsFiletypeDocx, BsFiletypeGif, BsFiletypeJpg, BsFiletypeMov, BsFiletypeMp3, BsFiletypeMp4, BsFiletypePdf, BsFiletypePng, BsFiletypePpt, BsFiletypePptx, BsFiletypePsd, BsFiletypeSvg, BsFiletypeTiff, BsFiletypeTxt, BsFiletypeXls, BsFiletypeXlsx, BsFiletypeXml } from "react-icons/bs"
import { getNumbersByString } from "./formatter"
import Logo from "../logo.svg"
import ImageWithFallback from "../components/ImageWithFallback"
import { useContactStore } from "../stores/useContactStore"
import { whatsAppEmojiMap } from "../components/Chat/WhatsAppEmojiManifest"
import { getWhatsAppEmojiAssetUrlByCode } from "../components/Chat/whatsAppEmojiAssets"
import { getUsersBySelectedTeams, getValidUserIdsByTeams } from "./teamUsers"

const verifyIfDateIsValid = (dateString) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/
    if (!regex.test(dateString)) return false

    const date = new Date(dateString)
    return !isNaN(date) && date?.toISOString()?.slice(0, 10) === dateString
}

const getOptionByOptionList = (value, optionList) => {
    let selected = []
    optionList?.forEach(option => {
        if (Array.isArray(value)) {
            value?.forEach(item => {
                if (option?.id?.toString() === item?.toString()) {
                    selected.push(option.value)
                }
            })
        } else {
            if (option?.id?.toString() === value?.toString()) {
                selected.push(option.value)
            }
        }
    })

    return selected.join(", ")
}

const getIconByExtensionFile = (name) => {
    const nameSplit = name.split(".")
    const ext = nameSplit[nameSplit.length - 1]?.toLowerCase()
    switch (ext) {
        case "ai":
            return <BsFiletypeAi />
        case "bmp":
            return <BsFiletypeBmp />
        case "csv":
            return <BsFiletypeCsv />
        case "doc":
            return <BsFiletypeDoc />
        case "docx":
            return <BsFiletypeDocx />
        case "gif":
            return <BsFiletypeGif />
        case "jpg":
            return <BsFiletypeJpg />
        case "jpeg":
            return <BsFiletypeJpg />
        case "mov":
            return <BsFiletypeMov />
        case "mp3":
            return <BsFiletypeMp3 />
        case "mp4":
            return <BsFiletypeMp4 />
        case "pdf":
            return <BsFiletypePdf />
        case "png":
            return <BsFiletypePng />
        case "ppt":
            return <BsFiletypePpt />
        case "pptx":
            return <BsFiletypePptx />
        case "psd":
            return <BsFiletypePsd />
        case "svg":
            return <BsFiletypeSvg />
        case "tiff":
            return <BsFiletypeTiff />
        case "txt":
            return <BsFiletypeTxt />
        case "xls":
            return <BsFiletypeXls />
        case "xlsx":
            return <BsFiletypeXlsx />
        case "xml":
            return <BsFiletypeXml />
        default:
            return <BsFileEarmark />
    }
}

const openSelectSingleFile = (handleChange = () => { }, accept = "*") => {
    const input = document.createElement("input")
    input.setAttribute("type", "file")
    input.setAttribute("accept", accept)
    input.addEventListener("change", setFile)
    input.click()

    function setFile(e) {
        const file = e.target.files[0]
        handleChange(file)
    }
}

const areObjectsEqual = (obj1, obj2) => {
    const stringifiedObj1 = JSON.stringify(obj1)
    const stringifiedObj2 = JSON.stringify(obj2)
    return stringifiedObj1 === stringifiedObj2
}

const getAddressByZipCode = async (zipCode) => {
    try {
        const zipCodeonlyNumbers = getNumbersByString(zipCode)
        if (zipCodeonlyNumbers.length >= 8) {
            const url = `https://viacep.com.br/ws/${zipCodeonlyNumbers}/json/`
            const response = await fetch(url)
            const data = await response.json()
            const newAddress = {
                country: "Brasil",
                uf: data.uf,
                city: data.localidade,
                district: data.bairro,
                road: data.logradouro,
            }
            return newAddress
        }
        return {}
    } catch (err) {
        console.log(err)
        return {}
    }
}

const showNotification = (title, description, link, icon = Logo, data = {}) => {
    if ('Notification' in window) {
        Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
                const notification = new Notification(title, {
                    body: description,
                    data,
                    icon,
                })
                if (link) {
                    notification.onclick = () => {
                        window.open(link, '_blank')
                    }
                }
            }
        })
    }
}

const isImageValid = (url) => {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve(true)
        img.onerror = () => resolve(false)
        img.src = url
    })
}

function resizeImage(file, maxWidth, maxHeight, callback) {
    const img = new Image()
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    const reader = new FileReader()

    reader.onload = (e) => {
        img.src = e.target.result
    }

    img.onload = () => {
        let width = img.width
        let height = img.height

        if (width > height) {
            if (width > maxWidth) {
                height *= maxWidth / width
                width = maxWidth
            }
        } else {
            if (height > maxHeight) {
                width *= maxHeight / height
                height = maxHeight
            }
        }

        canvas.width = width
        canvas.height = height
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob((blob) => {
            callback(blob)
        }, file.type)
    }

    reader.readAsDataURL(file)
}

const createRoundedImage = (imageUrl, size = 128, borderRadius = size / 2) => {
    return new Promise((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = size
            canvas.height = size
            const ctx = canvas.getContext('2d')

            ctx.beginPath()
            ctx.moveTo(borderRadius, 0)
            ctx.lineTo(size - borderRadius, 0)
            ctx.quadraticCurveTo(size, 0, size, borderRadius)
            ctx.lineTo(size, size - borderRadius)
            ctx.quadraticCurveTo(size, size, size - borderRadius, size)
            ctx.lineTo(borderRadius, size)
            ctx.quadraticCurveTo(0, size, 0, size - borderRadius)
            ctx.lineTo(0, borderRadius)
            ctx.quadraticCurveTo(0, 0, borderRadius, 0)
            ctx.closePath()
            ctx.clip()

            ctx.drawImage(img, 0, 0, size, size)

            const roundedImageUrl = canvas.toDataURL('image/png')
            resolve(roundedImageUrl)
        }

        img.onerror = () => {
            resolve(imageUrl)
        }

        img.src = imageUrl
    })
}

function generateColorPalette(baseColorsInput, count, options = {}) {
    const defaultColors = ['#38d1e5', '#ff9b32', '#6245ae', '#0a9c22', '#d2417c']
    const baseColors = Array.isArray(baseColorsInput) && baseColorsInput.length > 0
        ? baseColorsInput
        : defaultColors

    const {
        minLightness = 25,
        maxLightness = 85,
        variationLightnessStep = 10,
        hueStep = 30
    } = options

    const palette = []
    const baseCount = baseColors.length

    for (let i = 0; i < count; i++) {
        const baseHex = baseColors[i % baseCount]
        const variation = Math.floor(i / baseCount)

        const { h, s, l } = hexToHSL(baseHex)

        const rotatedHue = (h + variation * hueStep) % 360
        const adjustedL = Math.max(minLightness, Math.min(maxLightness, l - variation * variationLightnessStep))

        palette.push(hslToHex(rotatedHue, s, adjustedL))
    }

    return palette
}

function hexToHSL(hex) {
    let r = 0, g = 0, b = 0
    if (hex.length === 7) {
        r = parseInt(hex.slice(1, 3), 16) / 255
        g = parseInt(hex.slice(3, 5), 16) / 255
        b = parseInt(hex.slice(5, 7), 16) / 255
    }

    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    let h = 0, s = 0, l = (max + min) / 2

    if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)); break
            case g: h = ((b - r) / d + 2); break
            case b: h = ((r - g) / d + 4); break
            default: break
        }
        h = h * 60
    }

    return { h, s: s * 100, l: l * 100 }
}

function hslToHex(h, s, l) {
    s /= 100
    l /= 100

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs((h / 60) % 2 - 1))
    const m = l - c / 2
    let r = 0, g = 0, b = 0

    if (0 <= h && h < 60) [r, g, b] = [c, x, 0]
    else if (60 <= h && h < 120) [r, g, b] = [x, c, 0]
    else if (120 <= h && h < 180) [r, g, b] = [0, c, x]
    else if (180 <= h && h < 240) [r, g, b] = [0, x, c]
    else if (240 <= h && h < 300) [r, g, b] = [x, 0, c]
    else if (300 <= h && h < 360) [r, g, b] = [c, 0, x]

    r = Math.round((r + m) * 255)
    g = Math.round((g + m) * 255)
    b = Math.round((b + m) * 255)

    return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`
}

const getUniqueId = () => {
    return Math.random()?.toString(16).slice(2) + new Date().getTime() + Math.random()?.toString(16).slice(2)
}

const adjustOptionsProduct = async (arrayProducts) => {
    return new Promise(resolve => {
        const productList = arrayProducts?.filter(product => product.last_child)?.map(product => {
            if (product.father) {
                product = getFatherByProduct(arrayProducts, product)
                return product
            } else {
                return product
            }
        })
        resolve(productList)
    })
}

const getFatherByProduct = (arrayProducts, item) => {
    let father = arrayProducts?.filter(product => product._id === item.father)
    if (father) {
        father.child = item
        father = { ...father[0], child: father.child }
        return father.father ? getFatherByProduct(arrayProducts, father) : father
    }
}

const getIdByProduct = (product) => {
    return product.child ? getIdByProduct(product.child) : product._id
}

const getNameByProduct = (product) => {
    let name = product.name ? (product.last_child ? product.name : `${product.name?.substring(0, 4)}.->`) : ''
    if (product.child) {
        name += getNameByProduct(product.child)
    }
    return name
}

const getNameFunnelById = (funnel_id, funnels) => {
    const funnel = getItemForArrayById(funnels, "id", funnel_id)
    return funnel?.value || ''
}

const getIdsUsersByTeamId = (team_id, teams) => {
    if (team_id) {
        const [usersTeam] = teams?.filter(team => team.id === team_id)?.map(team => { return team.users })
        const idsUsersTeam = usersTeam?.map(user => { return user.user_id })
        return idsUsersTeam
    }
    return []
}

const getUsersByTeamId = (team_id, users, teams, initialData = [], orCompare = () => { }) => {
    const idsUsersTeam = getIdsUsersByTeamId(team_id, teams)
    const listUsers = users?.filter(user => orCompare(user) || idsUsersTeam?.indexOf(user.id) !== -1) || []
    return [...initialData, ...listUsers]
}

const getUsersByTeams = (selectedTeams, users, teams, initialData = [], orCompare = () => { }) => {
    let listIdsUsers = []
    const filteredTeams = teams?.filter(team => orCompare(team) || selectedTeams?.indexOf(team.id) !== -1) || []
    filteredTeams?.forEach(team => {
        const idsUsersTeam = getIdsUsersByTeamId(team?.id, teams)
        const filteredIdsUsers = idsUsersTeam?.filter(user => listIdsUsers?.indexOf(user) === -1) || []
        listIdsUsers.push(...filteredIdsUsers)
    })
    const filteredUsers = users?.filter(user => orCompare(user) || listIdsUsers?.indexOf(user.id) !== -1) || []
    return [...initialData, ...filteredUsers]
}

const getItemForArrayById = (array, propertyCompare, propertySearch) => {
    if (!Array.isArray(array)) return
    const [el] = array?.filter(el => el?.[propertyCompare] === propertySearch)
    return el
}

const getPropertyForItemInArrayById = (search, array, propertyCompare, propertySearch) => {
    if (!Array.isArray(array)) return ''
    const [el] = array?.filter(el => el?.[propertyCompare] === search)
    return el ? el?.[propertySearch] || '' : ''
}

const isValidAccountId = (accountId) => {
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    return objectIdRegex.test(accountId);
}

const openLinkExternal = (link_external, download = false, target = "_blank") => {
    const link = document.createElement("a")
    link.setAttribute("href", link_external)
    link.setAttribute("target", target)
    link.setAttribute("rel", "noopener noreferrer")
    if (download) {
        link.setAttribute("download", "true")
    }
    link.click()
}

// const adjustTextByIndentation = (text, account_id) => {
//     const listText = text?.split("\n");
//     let currentIndentLevel = 0;

//     const parseFormattedText = (line) => {
//         const elements = [];
//         let lastIndex = 0;

//         const regex = /(\*(.*?)\*)|(^|[^\w])_(.*?)_([^\w]|$)|(~(.*?)~)|(\[(.*?)\]\((.*?)\))|((https?:\/\/[^\s]+))|(@(\d{10,15})(?:@[\w.]+)?)/g;
//         let match;

//         while ((match = regex.exec(line)) !== null) {
//             if (match.index > lastIndex) {
//                 elements.push(line.slice(lastIndex, match.index));
//             }

//             if (match[2]) {
//                 elements.push(<strong key={match.index}>{match[2]}</strong>);
//             } else if (match[4]) {
//                 elements.push(<em key={match.index}>{match[4]}</em>);
//             } else if (match[6]) {
//                 elements.push(<s key={match.index}>{match[6]}</s>);
//             } else if (match[9] && match[10]) {
//                 elements.push(
//                     <a href={match[10]} target="_blank" rel="noopener noreferrer" key={match.index}>
//                         {match[9]}
//                     </a>
//                 );
//             } else if (match[11]) {
//                 elements.push(
//                     <a href={match[11]} target="_blank" rel="noopener noreferrer" key={match.index}>
//                         {match[11]}
//                     </a>
//                 );
//             } else if (match[13]) {
//                 const raw = match[14]

//                 const normalized = raw?.replace(/\D/g, '')

//                 const { contactsByIdentity = {} } = useContactStore.getState() || {}

//                 const contact = contactsByIdentity?.[raw] || contactsByIdentity?.[normalized]
//                 const name = contact?.name || raw
//                 const normalizedName = name?.replace(/\D/g, '')
//                 const isNumber = normalizedName.length > 0 && normalizedName === name.replace(/\D/g, '')
//                 const original_name = isNumber ? contact?.original_name : undefined
//                 const link = `/${account_id}/chat?action=startChat&phone=${normalized}`

//                 elements.push(
//                     <a
//                         href={link}
//                         className="mention"
//                         key={match.index}
//                         target="_blank"
//                         rel="noopener noreferrer"
//                     >
//                         @{name}{original_name ? ` (${original_name})` : ""}
//                     </a>
//                 )
//             }

//             lastIndex = regex.lastIndex;
//         }

//         if (lastIndex < line.length) {
//             elements.push(line.slice(lastIndex));
//         }

//         return elements;
//     };

//     return listText?.map((line, index) => {
//         const formattedLine = line?.replace("\r", "").trim();

//         if (formattedLine === "") {
//             return <br key={index} />;
//         }

//         if (formattedLine.replace(/^[-*]\s/, "").startsWith("**")) {
//             currentIndentLevel = 0;
//         }

//         if (formattedLine.startsWith("**")) {
//             return <p key={index}>{parseFormattedText(formattedLine)}</p>;
//         }

//         if (/^[-*]\s/.test(formattedLine)) {
//             currentIndentLevel = formattedLine.replace(/^[-*]\s/, "").startsWith("**") ? 0 : 1;
//             const childListClass = `list-level-${currentIndentLevel}`;
//             return (
//                 <ul key={index} className={childListClass}>
//                     <li>{parseFormattedText(formattedLine.replace(/^[-*]\s/, ""))}</li>
//                 </ul>
//             );
//         }

//         currentIndentLevel = 0;

//         return (
//             <p key={index} style={{ paddingLeft: "0px" }}>
//                 {parseFormattedText(formattedLine)}
//             </p>
//         );
//     });
// }

const adjustTextByIndentation = (text, account_id, options = {}) => {
    return formatBlockStructure(text, account_id, options)
}

const getWhatsAppEmojiCode = (emoji = "") => {
    try {
        return toCodePoints(`${emoji}`.indexOf(String.fromCharCode(0x200d)) < 0 ? `${emoji}`.replace(/\uFE0F/g, "") : `${emoji}`)?.join("-")
    } catch (err) {
        return Array.from(`${emoji}`.replace(/\uFE0F/g, ""))
            .map(char => char.codePointAt(0)?.toString(16))
            .filter(Boolean)
            .join("-")
    }
}

const getWhatsAppEmojiUrl = (emoji = "", size = 30) => {
    const exactEmoji = whatsAppEmojiMap[emoji]
    if (exactEmoji?.url) return exactEmoji.url
    if (exactEmoji?.code) return getWhatsAppEmojiAssetUrlByCode(exactEmoji.code)

    const withoutVariation = `${emoji}`.replace(/\uFE0F/g, "")
    const emojiWithoutVariation = whatsAppEmojiMap[withoutVariation]
    if (emojiWithoutVariation?.url) return emojiWithoutVariation.url
    if (emojiWithoutVariation?.code) return getWhatsAppEmojiAssetUrlByCode(emojiWithoutVariation.code)

    const withVariation = `${emoji}\uFE0F`
    const emojiWithVariation = whatsAppEmojiMap[withVariation]
    if (emojiWithVariation?.url) return emojiWithVariation.url
    if (emojiWithVariation?.code) return getWhatsAppEmojiAssetUrlByCode(emojiWithVariation.code)

    const code = getWhatsAppEmojiCode(emoji)
    return getWhatsAppEmojiAssetUrlByCode(code)
}

const isEmojiSegment = (segment = "") => {
    return /\p{Extended_Pictographic}/u.test(segment) || /[\u{1F1E6}-\u{1F1FF}]/u.test(segment)
}

const splitTextByGrapheme = (text = "") => {
    if (typeof Intl !== "undefined" && Intl.Segmenter) {
        return Array.from(new Intl.Segmenter("pt-BR", { granularity: "grapheme" }).segment(text), item => item.segment)
    }

    return Array.from(text)
}

const renderWhatsAppEmojiText = (text = "", keyPrefix = "text") => {
    const elements = []
    let plainText = ""

    splitTextByGrapheme(text).forEach((segment, index) => {
        if (!isEmojiSegment(segment)) {
            plainText += segment
            return
        }

        if (plainText) {
            elements.push(plainText)
            plainText = ""
        }

        elements.push(
            <span
                key={`${keyPrefix}-emoji-${index}`}
                aria-label={segment}
                style={{ display: "inline-flex", alignItems: "center", verticalAlign: "-0.2em" }}
            >
                <img
                    src={getWhatsAppEmojiUrl(segment)}
                    alt={segment}
                    style={{ width: "1.25em", height: "1.25em", display: "inline-block" }}
                    onError={(e) => {
                        e.currentTarget.style.display = "none"
                        if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = "inline"
                        }
                    }}
                />
                <span style={{ display: "none" }}>{segment}</span>
            </span>
        )
    })

    if (plainText) {
        elements.push(plainText)
    }

    return elements
}

const renderPlainTextSegment = (text = "", keyPrefix = "text", options = {}) => {
    return options?.renderWhatsAppEmojis ? renderWhatsAppEmojiText(text, keyPrefix) : text
}

const parseTextSegment = (text = "", keyPrefix = "text", options = {}) => {
    const query = `${options?.highlightQuery || ""}`.trim()
    if (query.length < 2) return renderPlainTextSegment(text, keyPrefix, options)

    const normalizedText = `${text}`.toLocaleLowerCase("pt-BR")
    const normalizedQuery = query.toLocaleLowerCase("pt-BR")
    const elements = []
    let cursor = 0
    let matchIndex = normalizedText.indexOf(normalizedQuery)

    while (matchIndex >= 0) {
        if (matchIndex > cursor) {
            elements.push(renderPlainTextSegment(text.slice(cursor, matchIndex), `${keyPrefix}-before-${cursor}`, options))
        }
        const matchEnd = matchIndex + normalizedQuery.length
        elements.push(
            <mark className="message-search-highlight" key={`${keyPrefix}-match-${matchIndex}`}>
                {renderPlainTextSegment(text.slice(matchIndex, matchEnd), `${keyPrefix}-match-text-${matchIndex}`, options)}
            </mark>
        )
        cursor = matchEnd
        matchIndex = normalizedText.indexOf(normalizedQuery, cursor)
    }

    if (cursor < text.length) {
        elements.push(renderPlainTextSegment(text.slice(cursor), `${keyPrefix}-after-${cursor}`, options))
    }

    return elements.length ? elements : renderPlainTextSegment(text, keyPrefix, options)
}

const parseInlineFormatting = (line, account_id, options = {}) => {
    const elements = []
    let lastIndex = 0

    const regex = /(```([\s\S]*?)```)|(\*(.*?)\*)|(^|[^\w])_(.*?)_([^\w]|$)|(~(.*?)~)|(\[(.*?)\]\((.*?)\))|((https?:\/\/[^\s]+))|(@(\d{10,15})(?:@[\w.]+)?)/g

    let match

    while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIndex) {
            elements.push(parseTextSegment(line.slice(lastIndex, match.index), `plain-${lastIndex}`, options))
        }

        if (match[2]) {
            elements.push(<code key={match.index}>{parseTextSegment(match[2], `code-${match.index}`, options)}</code>)
        } else if (match[4]) {
            elements.push(<strong key={match.index}>{parseTextSegment(match[4], `bold-${match.index}`, options)}</strong>)
        } else if (match[6]) {
            elements.push(
                <span key={match.index} style={{ display: "contents" }}>
                    {match[5]}
                    <em>{parseTextSegment(match[6], `italic-${match.index}`, options)}</em>
                    {match[7]}
                </span>
            )
        } else if (match[9]) {
            elements.push(<s key={match.index}>{parseTextSegment(match[9], `strike-${match.index}`, options)}</s>)
        } else if (match[11] && match[12]) {
            elements.push(
                <a href={match[12]} target="_blank" rel="noopener noreferrer" key={match.index}>
                    {match[11]}
                </a>
            )
        } else if (match[13]) {
            elements.push(
                <a href={match[13]} target="_blank" rel="noopener noreferrer" key={match.index}>
                    {match[13]}
                </a>
            )
        } else if (match[15]) {
            const raw = match[16]
            const normalized = raw?.replace(/\D/g, '')

            const { contactsByIdentity = {} } = useContactStore.getState() || {}
            const contact = contactsByIdentity?.[raw] || contactsByIdentity?.[normalized]

            const name = contact?.name || raw
            const normalizedName = name?.replace(/\D/g, '')
            const isNumber = normalizedName.length > 0 && normalizedName === name.replace(/\D/g, '')

            const original_name = isNumber ? contact?.original_name : undefined
            const link = `/${account_id}/chat?action=startChat&phone=${normalized}`

            elements.push(
                <a
                    href={link}
                    className="mention"
                    key={match.index}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    @{name}{original_name ? ` (${original_name})` : ""}
                </a>
            )
        }

        lastIndex = regex.lastIndex
    }

    if (lastIndex < line.length) {
        elements.push(parseTextSegment(line.slice(lastIndex), `plain-${lastIndex}`, options))
    }

    return elements
}

const formatBlockStructure = (text, account_id, options = {}) => {
    const lines = text?.split("\n")
    let currentIndentLevel = 0

    return lines?.map((line, index) => {
        const formattedLine = line?.replace("\r", "").trim()

        if (formattedLine === "") {
            return <br key={index} />
        }

        if (formattedLine.replace(/^[-*]\s/, "").startsWith("**")) {
            currentIndentLevel = 0
        }

        if (formattedLine.startsWith("**")) {
            return <p key={index}>{parseInlineFormatting(formattedLine, account_id, options)}</p>
        }

        if (/^[-*]\s/.test(formattedLine)) {
            currentIndentLevel = formattedLine.replace(/^[-*]\s/, "").startsWith("**") ? 0 : 1

            return (
                <ul key={index} className={`list-level-${currentIndentLevel}`}>
                    <li>
                        {parseInlineFormatting(formattedLine.replace(/^[-*]\s/, ""), account_id, options)}
                    </li>
                </ul>
            )
        }

        currentIndentLevel = 0

        return (
            <p key={index}>
                {parseInlineFormatting(formattedLine, account_id, options)}
            </p>
        )
    })
}

function getImgProfileByName(nameResponsible, styles = "") {
    const nameArray = nameResponsible?.split(" ")
    const initials = `${nameArray[0][0]}${nameArray[nameArray.length - 1][0]}`
    return (
        <div className={`imgResponsible ${styles ? styles : ''}`}>{initials}</div>
    )
}

function getProfileImageUserById(allUsersGroup, user_id, styles = "") {
    const [user] = allUsersGroup?.filter(el => el._id === user_id)
    return getProfileImageByUserName(user?.name, user?.image, styles)
}

function getProfileImageByUserName(name, image, styles = "") {
    return (
        <>
            {name && <span className="labelShowInHover">{name}</span>}
            {image &&
                <ImageWithFallback
                    src={image}
                    alt="Responsável pelo Lead"
                    functionError={() => getImgProfileByName(name, styles)}
                />
            }
            {!image && name && getImgProfileByName(name, styles)}
        </>
    )
}

function getProfileUserById(allUsersGroup, user_id) {
    const [user] = allUsersGroup?.filter(el => el._id === user_id)
    const image = getProfileImageUserById(allUsersGroup, user_id)
    return { name: user?.name, image }
}

function getNameUserById(allUsersGroup, user_id) {
    const [user] = allUsersGroup?.filter(el => el._id === user_id)
    return user?.name
}

function getLocalDate(date, format = "YYYY-MM-DD HH:mm:ss") {
    return date ? moment.utc(date, format).local() : moment.utc().local()
}

function formatRelativeDate(date) {
    if (!date) return ''

    const past = new Date(date)

    // valida se a data é válida
    if (isNaN(past.getTime())) return ''

    const now = new Date()
    let diff = Math.floor((now - past) / 1000)

    // evita datas no futuro
    if (diff < 0) return 'agora'

    if (diff < 10) return 'agora há pouco'
    if (diff < 60) return 'agora'

    const minutes = Math.floor(diff / 60)
    if (minutes < 60) return `há ${minutes} min`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `há ${hours} h`

    const days = Math.floor(hours / 24)
    if (days === 1) return 'ontem'
    if (days < 30) return `há ${days} dias`

    const months = Math.floor(days / 30)
    if (months < 12) return `há ${months} mês${months > 1 ? 'es' : ''}`

    const years = Math.floor(days / 365)
    return `há ${years} ano${years > 1 ? 's' : ''}`
}

function formatFullDate(date) {
    if (!date) return ''

    const d = new Date(date)
    if (isNaN(d.getTime())) return ''

    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

function wasRecentlyUpdated(createdAt, updatedAt, thresholdSeconds = 10) {
    if (!createdAt || !updatedAt) return false

    const created = new Date(createdAt)
    const updated = new Date(updatedAt)

    if (isNaN(created.getTime()) || isNaN(updated.getTime())) {
        return false
    }

    const diffInSeconds = Math.abs((updated - created) / 1000)

    return diffInSeconds > thresholdSeconds
}

function getDiffInSecondsByDates(date1, date2) {
    const diffInSeconds = date2.diff(date1, 'seconds')
    return diffInSeconds > 0 ? diffInSeconds : 0
}

async function getCoordinates(address) {
    const encodedAddress = encodeURIComponent(address)
    const apiKey = process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY

    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`

    try {
        const response = await fetch(url);
        const data = await response.json();

        if (data.status === 'OK') {
            const location = data.results[0].geometry.location
            const formatted_address = data.results[0].formatted_address
            // const place_id = data.results[0].place_id
            // const name = place_id ? await getPlaceName(place_id) : ""

            return { latitude: location.lat, longitude: location.lng, address: formatted_address }
        } else {
            console.error('Geocoding error:', data.status)
        }
    } catch (error) {
        console.error('Request failed:', error)
    }
}

// async function getPlaceName(place_id) {
//     const apiKey = process.env.REACT_APP_GOOGLE_PLACE_API_KEY

//     const url = `https://places.googleapis.com/v1/places/${place_id}?fields=id,displayName&key=${apiKey}`

//     try {
//         const response = await fetch(url)
//         if (!response.ok) {
//             throw new Error(`HTTP error! status: ${response.status}`)
//         }

//         const data = await response.json()
//         console.log(data)
//         return data.displayName?.text || null
//     } catch (error) {
//         console.error('Request failed:', error)
//     }
// }

async function getBlobByLocation(latitude, longitude) {
    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=15&size=100x100&markers=color:red%7C${latitude},${longitude}&format=jpg&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
    try {
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error('Network response was not ok')
        }
        const blob = await response.blob()
        return blob
    } catch (err) {
        console.error('Failed to fetch the blob:', err)
        throw err
    }
}

async function blobToBase64(blob) {
    try {
        const reader = new FileReader()
        return new Promise((resolve, reject) => {
            reader.onloadend = () => {
                const base64String = reader.result.split(',')[1]
                resolve(base64String)
            }
            reader.onerror = reject
            reader.readAsDataURL(blob)
        });
    } catch (err) {
        console.error('Failed to convert blob to base64:', err)
        throw err
    }
}

async function getThumbnailByLocation(latitude, longitude) {
    try {
        const blob = await getBlobByLocation(latitude, longitude)
        const base64 = await blobToBase64(blob)
        return base64
    } catch (err) {
        console.error('Failed to get thumbnail by location:', err)
    }
}

function sortValuesByPropertyAndDirection(a, b, property, direction = "ASC") {
    if (a[property] > b[property]) {
        return direction === "ASC" ? 1 : -1
    } else if (a[property] < b[property]) {
        return direction === "ASC" ? -1 : 1
    } else {
        return 0
    }
}

function capitalizeFirstLetter(string) {
    return string?.charAt(0)?.toUpperCase() + string?.slice(1)?.toLowerCase()
}

function sanitizeAndFormatText(inputText) {
    return inputText
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .toLowerCase()
}

const isColorDark = (hexColor) => {
    hexColor = hexColor.replace("#", "")

    let r = parseInt(hexColor?.substring(0, 2), 16)
    let g = parseInt(hexColor?.substring(2, 4), 16)
    let b = parseInt(hexColor?.substring(4, 6), 16)

    let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

    return luminance <= 0.5
}

const getFirstName = (fullName) => {
    if (!fullName) {
        return ""
    }
    return fullName.trim().split(" ")[0]
}

const safeMoment = (date, format = "YYYY-MM-DD") => {
    return moment.isMoment(date) ? date : moment(date, format)
}

const truncateText = (text, max = 50) => {
    if (!text) return ""
    return text.length > max ? text.slice(0, max) + "..." : text
}

const listMethodsPayment = [
    { id: "BANK_SLIP", value: "Boleto Bancário" },
    { id: "CREDIT_CARD", value: "Cartão de Crédito" },
    // { id: "DEBIT_CARD", value: "Cartão de Débito" },
    // { id: "PIX", value: "PIX" },
]

export { verifyIfDateIsValid, getOptionByOptionList, getIconByExtensionFile, openSelectSingleFile, areObjectsEqual, getAddressByZipCode, showNotification, isImageValid, createRoundedImage, generateColorPalette, getUniqueId, adjustOptionsProduct, getIdByProduct, getNameByProduct, getNameFunnelById, getIdsUsersByTeamId, getUsersByTeamId, getUsersByTeams, getUsersBySelectedTeams, getValidUserIdsByTeams, getItemForArrayById, getPropertyForItemInArrayById, isValidAccountId, openLinkExternal, adjustTextByIndentation, parseInlineFormatting, getWhatsAppEmojiUrl, getProfileImageUserById, getProfileImageByUserName, getProfileUserById, getNameUserById, resizeImage, getCoordinates, getThumbnailByLocation, getLocalDate, formatRelativeDate, formatFullDate, wasRecentlyUpdated, getDiffInSecondsByDates, sortValuesByPropertyAndDirection, capitalizeFirstLetter, sanitizeAndFormatText, isColorDark, getFirstName, listMethodsPayment, safeMoment, truncateText }
